// Enhanced Session Builder Service
// Builds personalized learning sessions with true spaced repetition and adaptive learning

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  increment,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

class SessionBuilderService {
  constructor() {
    this.maxQuestionsPerConcept = 2;
    this.maxReviewQuestions = 3;
    this.sessionLengthPreference = 16; // minutes
    this.subjects = ['Mathematics', 'Physics', 'Chemistry'];
    this.batchSize = 10; // Conservative batch size for 'in' queries
  }

  /**
   * Count total lesson nodes across provided subjects
   */
  async countTotalLessons(subjects) {
    try {
      let total = 0;
      const learningNodesRef = collection(db, 'learningNodes');
      for (const subject of subjects) {
        const q = query(
          learningNodesRef,
          where('nodeType', '==', 'lesson'),
          where('subjectName', '==', subject)
        );
        const snapshot = await getDocs(q);
        total += snapshot.size;
      }
      return total;
    } catch (error) {
      console.error('❌ Error counting total lessons:', error);
      return 0;
    }
  }
  /**
   * Get or create user progress with proper error handling
   */
  async getUserProgress(userId) {
    try {
      console.log('🔍 Getting user progress for:', userId);
      const userProgressRef = doc(db, 'userProgress', userId);
      const userProgressDoc = await getDoc(userProgressRef);
      
      if (!userProgressDoc.exists()) {
        console.log('⚠️ No user progress found, creating default for:', userId);
        // Create new user progress properly
        const defaultProgress = {
          currentPath: {
            currentNodeId: null, // Will be set to first available lesson
            completedNodes: [],
            nextNodeId: null,
            currentSubject: 'Mathematics',
            pathProgress: 0
          },
          questionHistory: {
            askedQuestions: [],
            questionPerformance: {}, // questionId -> { correct, responseTime, date }
            lastAskedDate: null,
            totalQuestionsAnswered: 0,
            correctAnswers: 0,
            averageResponseTime: 0
          },
          mastery: {}, // conceptId -> { level, lastReviewed, nextReview }
          sessionStats: {
            totalSessions: 0,
            totalTime: 0,
            averageScore: 0,
            lastSessionDate: null,
            averageSessionLength: 0
          },
          performance: {
            subjectAccuracy: {
              Mathematics: { correct: 0, total: 0, accuracy: 0 },
              Physics: { correct: 0, total: 0, accuracy: 0 },
              Chemistry: { correct: 0, total: 0, accuracy: 0 }
            },
            weakAreas: [],
            strongAreas: [],
            difficultyLevel: 'beginner'
          },
          preferences: {
            sessionLength: this.sessionLengthPreference,
            difficulty: 'adaptive',
            subjects: this.subjects,
            examDate: Timestamp.fromDate(new Date('2026-04-09T00:00:00'))
          },
          srsData: {}, // questionId -> { level, eFactor, intervalDays, repetitionCount, nextReview, lastReviewed }
          totalXP: 0,
          level: 1,
          streak: 0,
          lastStudyDate: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        // Save with setDoc instead of updateDoc
        await setDoc(userProgressRef, defaultProgress);
        console.log('✅ Created new user progress for:', userId);
        return defaultProgress;
      }
      
      const userData = userProgressDoc.data();
      console.log('📊 Existing user progress data structure:', {
        hasCurrentPath: !!userData.currentPath,
        hasQuestionHistory: !!userData.questionHistory,
        hasPreferences: !!userData.preferences,
        hasSrsData: !!userData.srsData,
        completedNodes: userData.currentPath?.completedNodes?.length || 0,
        askedQuestions: userData.questionHistory?.askedQuestions?.length || 0
      });
      
      return userData;
      
    } catch (error) {
      console.error('❌ Error getting user progress:', error);
      throw error;
    }
  }

  /**
   * Build a complete learning session with proper subject rotation and adaptive content
   */
  async buildSession(userId, userProgress) {
    console.log('🔧 Building enhanced session for user:', userId);
    console.log('📋 User progress summary:', {
      hasProgress: !!userProgress,
      currentSubject: userProgress?.currentPath?.currentSubject,
      completedNodes: userProgress?.currentPath?.completedNodes?.length || 0,
      preferences: userProgress?.preferences?.subjects || []
    });
    
    try {
      const session = {
        sessionId: `session_${Date.now()}_${userId}`,
        userId: userId,
        nodes: [],
        totalQuestions: 0,
        estimatedDuration: 0,
        subjectRotation: [],
        createdAt: new Date()
      };

      // 1. Get next lesson with proper subject rotation
      const nextLesson = await this.getNextLesson(userProgress);
      if (nextLesson) {
        console.log('📚 Next lesson:', nextLesson.conceptTitle, 'in', nextLesson.subjectName);
        
        session.nodes.push({
          nodeId: nextLesson.nodeId,
          nodeType: 'lesson',
          subjectName: nextLesson.subjectName,
          content: {
            conceptTitle: nextLesson.conceptTitle,
            detailedExplanation: nextLesson.detailedExplanation,
            chapterTitle: nextLesson.chapterTitle
          },
          metadata: {
            estimatedTime: nextLesson.estimatedTime || 10,
            difficulty: nextLesson.difficulty || 'beginner'
          }
        });

        // 2. Get practice questions with adaptive difficulty
        const practiceQuestions = await this.getAdaptiveQuestionsForNode(
          nextLesson.nodeId, 
          userId, 
          userProgress,
          'practice'
        );
        
        if (practiceQuestions.length > 0) {
          session.nodes.push({
            nodeId: `${nextLesson.nodeId}_practice`,
            nodeType: 'micro-practice',
            subjectName: nextLesson.subjectName,
            content: {
              questions: practiceQuestions.slice(0, this.maxQuestionsPerConcept)
            },
            metadata: {
              parentNodeId: nextLesson.nodeId,
              questionCount: Math.min(practiceQuestions.length, this.maxQuestionsPerConcept),
              difficulty: this.calculateAdaptiveDifficulty(userProgress, nextLesson.subjectName)
            }
          });
          session.totalQuestions += Math.min(practiceQuestions.length, this.maxQuestionsPerConcept);
        }
      }

      // 3. Get spaced repetition review questions
      const reviewQuestions = await this.getSpacedRepetitionReviews(userId, userProgress);
      if (reviewQuestions.length > 0) {
        session.nodes.push({
          nodeId: 'review_session',
          nodeType: 'review',
          subjectName: 'mixed',
          content: {
            questions: reviewQuestions.slice(0, this.maxReviewQuestions)
          },
          metadata: {
            questionCount: Math.min(reviewQuestions.length, this.maxReviewQuestions),
            reviewType: 'spaced_repetition',
            subjects: [...new Set(reviewQuestions.map(q => q.subjectName))]
          }
        });
        session.totalQuestions += Math.min(reviewQuestions.length, this.maxReviewQuestions);
      }

      // 4. Apply subject braiding logic
      session.nodes = await this.applySubjectBraiding(session.nodes, userProgress);

      // 5. Adjust session length to match user preference
      session.nodes = this.adjustSessionLength(session.nodes, userProgress.preferences.sessionLength);

      // 6. Recalculate totals after trimming
      session.totalQuestions = this.calculateTotalQuestions(session.nodes);
      session.estimatedDuration = this.calculateSessionDuration(session);

      console.log('✅ Enhanced session built successfully');
      console.log(`   - Total nodes: ${session.nodes.length}`);
      console.log(`   - Total questions: ${session.totalQuestions}`);
      console.log(`   - Estimated duration: ${session.estimatedDuration} minutes`);
      console.log(`   - Subjects covered: ${[...new Set(session.nodes.map(n => n.subjectName))].join(', ')}`);

      return session;

    } catch (error) {
      console.error('❌ Error building enhanced session:', error);
      throw error;
    }
  }

  /**
   * Get next lesson with proper subject rotation and ordering
   */
  async getNextLesson(userProgress) {
    try {
      const userSubjects = userProgress?.preferences?.subjects || this.subjects;
      const currentSubject = userProgress?.currentPath?.currentSubject || userSubjects[0];
      const currentNodeId = userProgress?.currentPath?.currentNodeId;
      const completedNodes = userProgress?.currentPath?.completedNodes || [];
      
      // Get all lesson nodes for the current subject, properly ordered
      const learningNodesRef = collection(db, 'learningNodes');
      const q = query(
        learningNodesRef,
        where('nodeType', '==', 'lesson'),
        where('subjectName', '==', currentSubject)
      );
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Sort by chapter and concept order (not by ID)
        const lessonNodes = snapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              nodeId: doc.id,
              ...data
            };
          })
          .sort((a, b) => {
            // Sort by chapter order first, then concept order
            if (a.originalChapterIndex !== b.originalChapterIndex) {
              return a.originalChapterIndex - b.originalChapterIndex;
            }
            return a.originalConceptIndex - b.originalConceptIndex;
          });

        let targetNode;
        
        if (!currentNodeId) {
          // Find first uncompleted lesson
          targetNode = lessonNodes.find(node => !completedNodes.includes(node.nodeId));
          if (!targetNode) {
            // All lessons completed, try next subject
            const nextSubject = this.getNextSubject(currentSubject, userSubjects);
            if (nextSubject !== currentSubject) {
              console.log(`🔄 All lessons completed in ${currentSubject}, rotating to ${nextSubject}`);
              return await this.getNextLesson({ 
                ...userProgress, 
                currentPath: { ...userProgress.currentPath, currentSubject: nextSubject } 
              });
            }
          }
          console.log('🆕 New user or resuming - starting with first uncompleted lesson:', targetNode?.conceptTitle);
        } else {
          // Find current node and get next one
          const currentIndex = lessonNodes.findIndex(node => node.nodeId === currentNodeId);
          if (currentIndex >= 0 && currentIndex < lessonNodes.length - 1) {
            targetNode = lessonNodes[currentIndex + 1];
            console.log('📖 Continuing to next lesson:', targetNode?.conceptTitle);
          } else if (currentIndex === lessonNodes.length - 1) {
            // Completed current subject, rotate to next subject
            const nextSubject = this.getNextSubject(currentSubject, userSubjects);
            console.log(`🔄 Completed ${currentSubject}, rotating to ${nextSubject}`);
            return await this.getNextLesson({ 
              ...userProgress, 
              currentPath: { ...userProgress.currentPath, currentSubject: nextSubject } 
            });
          }
        }
        
        if (targetNode) {
          return targetNode;
        }
      }
      
      // If no lessons found for current subject, try next subject
      const nextSubject = this.getNextSubject(currentSubject, userSubjects);
      if (nextSubject !== currentSubject) {
        console.log(`🔄 No lessons found for ${currentSubject}, trying ${nextSubject}`);
        return await this.getNextLesson({ 
          ...userProgress, 
          currentPath: { ...userProgress.currentPath, currentSubject: nextSubject } 
        });
      }
      
      console.log('🏁 No more lessons available in any subject');
      return null;
      
    } catch (error) {
      console.error('❌ Error getting next lesson:', error);
      return null;
    }
  }

  /**
   * Get adaptive questions with difficulty adjustment and reliable fallback
   */
  async getAdaptiveQuestionsForNode(nodeId, userId, userProgress, questionType = 'practice') {
    try {
      console.log(`🔍 Getting ${questionType} questions for node: ${nodeId}`);
      
      const userQuestionHistory = userProgress?.questionHistory?.askedQuestions || [];
      const questionPerformance = userProgress?.questionHistory?.questionPerformance || {};
      
      // Get questions for this node
      const questionsRef = collection(db, 'questions');
      const q = query(
        questionsRef,
        where('learningNodeId', '==', nodeId)
      );
      
      const snapshot = await getDocs(q);
      const allQuestions = snapshot.docs.map(doc => ({
        questionId: doc.id,
        ...doc.data()
      }));
      
      // Filter out recently asked questions (but allow wrong answers for re-practice)
      const unaskedQuestions = allQuestions.filter(q => 
        !userQuestionHistory.includes(q.questionId)
      );
      
             // Get questions user got wrong recently (for re-practice)
       const wrongQuestions = allQuestions.filter(q => {
         const performance = questionPerformance[q.questionId];
         if (!performance || performance.correct !== false || !performance.date) {
           return false;
         }
         
         // Convert Firestore Timestamp to Date for comparison
         const performanceDate = performance.date.toDate ? performance.date.toDate() : new Date(performance.date);
         const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
         
         return performanceDate > sevenDaysAgo; // Last 7 days
       });
      
      console.log(`   - Total questions for node: ${allQuestions.length}`);
      console.log(`   - Unasked questions: ${unaskedQuestions.length}`);
      console.log(`   - Recently wrong questions: ${wrongQuestions.length}`);
      
      let selectedQuestions = [];
      
      if (unaskedQuestions.length >= this.maxQuestionsPerConcept) {
        // We have enough unasked questions
        selectedQuestions = this.selectAdaptiveQuestions(unaskedQuestions, userProgress, questionType);
      } else if (unaskedQuestions.length > 0) {
        // Mix unasked and wrong questions
        selectedQuestions = unaskedQuestions;
        const neededQuestions = this.maxQuestionsPerConcept - unaskedQuestions.length;
        
        // Add wrong questions for re-practice
        const wrongQuestionsToAdd = wrongQuestions
          .filter(q => !selectedQuestions.find(sq => sq.questionId === q.questionId))
          .slice(0, neededQuestions);
        
        selectedQuestions.push(...wrongQuestionsToAdd);
        
        // If still need more, try ChatGPT generation
        if (selectedQuestions.length < this.maxQuestionsPerConcept) {
          const remainingNeeded = this.maxQuestionsPerConcept - selectedQuestions.length;
          const newQuestions = await this.generateQuestionsViaChatGPT(nodeId, remainingNeeded, userProgress);
          if (newQuestions.length > 0) {
            selectedQuestions.push(...newQuestions);
          }
        }
      } else {
        // No unasked questions, prioritize wrong questions then generate new ones
        selectedQuestions = wrongQuestions.slice(0, this.maxQuestionsPerConcept);
        
        if (selectedQuestions.length < this.maxQuestionsPerConcept) {
          const remainingNeeded = this.maxQuestionsPerConcept - selectedQuestions.length;
          const newQuestions = await this.generateQuestionsViaChatGPT(nodeId, remainingNeeded, userProgress);
          if (newQuestions.length > 0) {
            selectedQuestions.push(...newQuestions);
          }
        }
        
        // If still no questions, fallback to reusing all questions
        if (selectedQuestions.length === 0) {
          console.log('   - No questions available, falling back to reusing all questions');
          selectedQuestions = this.selectAdaptiveQuestions(allQuestions, userProgress, questionType);
        }
      }
      
      return selectedQuestions.slice(0, this.maxQuestionsPerConcept);
      
    } catch (error) {
      console.error('❌ Error getting adaptive questions:', error);
      return [];
    }
  }

  /**
   * Get spaced repetition review questions based on true SM-2 algorithm (optimized)
   */
  async getSpacedRepetitionReviews(userId, userProgress) {
    try {
      console.log('🔄 Getting spaced repetition reviews');
      
      const completedNodes = userProgress?.currentPath?.completedNodes || [];
      const srsData = userProgress?.srsData || {};
      const questionPerformance = userProgress?.questionHistory?.questionPerformance || {};
      
      if (completedNodes.length === 0) {
        console.log('   - No completed nodes for review');
        return [];
      }
      
      const dueReviews = [];
      
      // Batch query questions for all completed nodes (optimize N+1)
      const allQuestions = await this.getQuestionsForMultipleNodes(completedNodes);
      
             // Check each question's SRS data
       for (const question of allQuestions) {
         const questionSrsData = srsData[question.questionId] || {};
         
         // Convert Firestore Timestamps to Date objects for comparison
         const lastReviewed = questionSrsData.lastReviewed ? 
           (questionSrsData.lastReviewed.toDate ? questionSrsData.lastReviewed.toDate() : new Date(questionSrsData.lastReviewed)) : null;
         const nextReview = questionSrsData.nextReview ? 
           (questionSrsData.nextReview.toDate ? questionSrsData.nextReview.toDate() : new Date(questionSrsData.nextReview)) : null;
         
         // Check if review is due
         if (!nextReview || new Date() >= nextReview) {
          dueReviews.push({
            ...question,
            nodeId: question.learningNodeId,
            reviewType: 'spaced_repetition',
            srsLevel: questionSrsData.level || 1,
            daysSinceLastReview: lastReviewed ? Math.floor((new Date() - lastReviewed) / (1000 * 60 * 60 * 24)) : null,
            priority: this.calculateReviewPriority(question, questionPerformance, questionSrsData)
          });
        }
      }
      
      // Sort by priority (wrong answers first, then by SRS level and recency)
      dueReviews.sort((a, b) => {
        if (a.priority !== b.priority) return b.priority - a.priority;
        return (b.daysSinceLastReview || 0) - (a.daysSinceLastReview || 0);
      });
      
      console.log(`   - Found ${dueReviews.length} due reviews`);
      return dueReviews;
      
    } catch (error) {
      console.error('❌ Error getting spaced repetition reviews:', error);
      return [];
    }
  }

  /**
   * Get questions for multiple nodes in batches (optimize N+1 queries)
   */
  async getQuestionsForMultipleNodes(nodeIds) {
    const allQuestions = [];
    
         // Process in configurable batches (conservative for compatibility)
     for (let i = 0; i < nodeIds.length; i += this.batchSize) {
       const batch = nodeIds.slice(i, i + this.batchSize).filter(id => id && id.trim()); // Guard against empty/undefined IDs
       
       if (batch.length === 0) continue; // Skip empty batches
       
       const questionsRef = collection(db, 'questions');
       const q = query(
         questionsRef,
         where('learningNodeId', 'in', batch)
       );
      
      const snapshot = await getDocs(q);
      const batchQuestions = snapshot.docs.map(doc => ({
        questionId: doc.id,
        ...doc.data()
      }));
      
      allQuestions.push(...batchQuestions);
    }
    
    return allQuestions;
  }

  /**
   * Calculate review priority based on performance and SRS data
   */
  calculateReviewPriority(question, questionPerformance, srsData) {
    const performance = questionPerformance[question.questionId];
    let priority = 0;
    
    // Wrong answers get highest priority
    if (performance?.correct === false) {
      priority += 100;
    }
    
    // Lower SRS levels get higher priority (need more frequent review)
    priority += (10 - (srsData.level || 1)) * 10;
    
         // Older reviews get higher priority
     if (srsData.lastReviewed) {
       // Convert Firestore Timestamp to Date for proper comparison
       const lastReviewedDate = srsData.lastReviewed.toDate ? srsData.lastReviewed.toDate() : new Date(srsData.lastReviewed);
       const daysSince = Math.floor((new Date() - lastReviewedDate) / (1000 * 60 * 60 * 24));
       priority += Math.min(daysSince, 30); // Cap at 30 days
     }
    
    return priority;
  }

  /**
   * Generate new questions via ChatGPT API
   */
  async generateQuestionsViaChatGPT(nodeId, count, userProgress) {
    try {
      console.log(`🤖 Generating ${count} new questions via ChatGPT for node: ${nodeId}`);
      
      // Get the learning node to understand the concept
      const nodeRef = doc(db, 'learningNodes', nodeId);
      const nodeDoc = await getDoc(nodeRef);
      
      if (!nodeDoc.exists()) {
        console.log('❌ Node not found for question generation');
        return [];
      }
      
      const nodeData = nodeDoc.data();
      
      // Create prompt for ChatGPT
      const prompt = this.createQuestionGenerationPrompt(nodeData, count);
      
      // TODO: Implement ChatGPT API call
      // For now, return empty array (placeholder)
      console.log('📝 ChatGPT integration pending - would generate questions for:', nodeData.conceptTitle);
      
      // Log that this node needs more questions
      await this.logQuestionShortage(nodeId, nodeData.conceptTitle, count);
      
      // When implemented, this would:
      // 1. Call ChatGPT API with the prompt
      // 2. Parse the response into question objects
      // 3. Save new questions to Firebase
      // 4. Return the new questions
      
      return [];
      
    } catch (error) {
      console.error('❌ Error generating questions via ChatGPT:', error);
      return [];
    }
  }

  /**
   * Log when a learning node runs out of questions
   */
  async logQuestionShortage(nodeId, conceptTitle, questionsNeeded) {
    try {
      const shortageLogRef = collection(db, 'questionShortageLogs');
      await addDoc(shortageLogRef, {
        learningNodeId: nodeId,
        conceptTitle: conceptTitle,
        questionsNeeded: questionsNeeded,
        timestamp: serverTimestamp(),
        status: 'pending', // pending, resolved
        priority: 'medium' // low, medium, high based on frequency
      });
      
      console.log(`📊 Logged question shortage for: ${conceptTitle} (${nodeId})`);
    } catch (error) {
      console.error('❌ Error logging question shortage:', error);
    }
  }

  /**
   * Create prompt for ChatGPT question generation
   */
  createQuestionGenerationPrompt(nodeData, count) {
    return `
Generate ${count} multiple-choice questions for the following concept:

Concept: ${nodeData.conceptTitle}
Subject: ${nodeData.subjectName}
Explanation: ${nodeData.detailedExplanation}

Requirements:
- Questions should be at ${nodeData.difficulty || 'beginner'} difficulty level
- Each question should have 4 options (A, B, C, D)
- Only one correct answer per question
- Include explanations for correct answers
- Questions should test understanding, not just memorization
- Format as JSON array with fields: questionText, options (array with id, text, correct), explanation

Generate questions that are similar but not identical to typical MH-CET exam questions.
`;
  }

  /**
   * Select questions based on adaptive difficulty
   */
  selectAdaptiveQuestions(questions, userProgress, questionType) {
    // Get computed difficulty for the current subject
    const currentSubject = userProgress?.currentPath?.currentSubject || 'Mathematics';
    const difficultyLevel = this.calculateAdaptiveDifficulty(userProgress, currentSubject);
    
    // Sort questions by difficulty and performance
    const sortedQuestions = questions.sort((a, b) => {
      // Prioritize questions user got wrong
      const aPerformance = userProgress?.questionHistory?.questionPerformance?.[a.questionId];
      const bPerformance = userProgress?.questionHistory?.questionPerformance?.[b.questionId];
      
      if (aPerformance?.correct === false && bPerformance?.correct !== false) return -1;
      if (bPerformance?.correct === false && aPerformance?.correct !== false) return 1;
      
      // Then by difficulty (easier first for struggling students)
      const aDifficulty = this.getQuestionDifficulty(a);
      const bDifficulty = this.getQuestionDifficulty(b);
      
      if (difficultyLevel === 'beginner') {
        return aDifficulty - bDifficulty;
      } else {
        return bDifficulty - aDifficulty;
      }
    });
    
    return sortedQuestions;
  }

  /**
   * Calculate adaptive difficulty based on performance
   */
  calculateAdaptiveDifficulty(userProgress, subjectName) {
    const subjectAccuracy = userProgress?.performance?.subjectAccuracy?.[subjectName];
    
    if (!subjectAccuracy || subjectAccuracy.total === 0) {
      return 'beginner';
    }
    
    const accuracy = subjectAccuracy.accuracy;
    
    if (accuracy >= 0.8) return 'advanced';
    if (accuracy >= 0.6) return 'intermediate';
    return 'beginner';
  }

  /**
   * Get question difficulty based on metadata
   */
  getQuestionDifficulty(question) {
    // Base difficulty on exam year, question type, and other metadata
    let difficulty = 1;
    
    // Newer exam questions might be harder
    if (question.examYear && question.examYear >= 2020) {
      difficulty += 0.5;
    }
    
    // Questions with hints might be easier
    if (question.hint) {
      difficulty -= 0.2;
    }
    
    // Questions with detailed explanations might be easier
    if (question.explanation && question.explanation.length > 100) {
      difficulty -= 0.1;
    }
    
    // Questions with solution steps might be easier
    if (question.solutionSteps && question.solutionSteps.length > 0) {
      difficulty -= 0.2;
    }
    
    return Math.max(0.5, Math.min(3, difficulty)); // Clamp between 0.5 and 3
  }

  /**
   * Apply subject braiding logic with actual implementation
   */
  async applySubjectBraiding(nodes, userProgress) {
    const userSubjects = userProgress?.preferences?.subjects || this.subjects;
    
    // Count subjects in current session
    const subjectCounts = {};
    nodes.forEach(node => {
      if (node.subjectName && node.subjectName !== 'mixed') {
        subjectCounts[node.subjectName] = (subjectCounts[node.subjectName] || 0) + 1;
      }
    });
    
    // If one subject dominates, try to add content from other subjects
    const maxCount = Math.max(...Object.values(subjectCounts));
    const dominantSubject = Object.keys(subjectCounts).find(subject => subjectCounts[subject] === maxCount);
    
    if (dominantSubject && maxCount > 2 && userSubjects.length > 1) {
      console.log(`🔄 Subject braiding: ${dominantSubject} dominates, adding content from other subjects`);
      
      // Find other subjects to add
      const otherSubjects = userSubjects.filter(subject => subject !== dominantSubject);
      
      // Try to add review questions from other subjects
      for (const otherSubject of otherSubjects) {
        if (nodes.length < 5) { // Don't make sessions too long
          // Get review questions from other subject
          const otherSubjectReviews = await this.getReviewQuestionsFromSubject(otherSubject, userProgress);
          if (otherSubjectReviews.length > 0) {
            // Add 1-2 review questions from other subject
            const reviewNode = {
              nodeId: `review_${otherSubject.toLowerCase()}`,
              nodeType: 'review',
              subjectName: otherSubject,
              content: {
                questions: otherSubjectReviews.slice(0, 2)
              },
              metadata: {
                questionCount: Math.min(otherSubjectReviews.length, 2),
                reviewType: 'cross_subject_braiding',
                sourceSubject: otherSubject
              }
            };
            
            // Insert after the lesson but before existing review
            const lessonIndex = nodes.findIndex(n => n.nodeType === 'lesson');
            const reviewIndex = nodes.findIndex(n => n.nodeType === 'review');
            const insertIndex = reviewIndex > -1 ? reviewIndex : lessonIndex + 1;
            
            nodes.splice(insertIndex, 0, reviewNode);
            console.log(`   - Added ${reviewNode.metadata.questionCount} review questions from ${otherSubject}`);
            break; // Only add from one other subject to avoid overcrowding
          }
        }
      }
    }
    
    return nodes;
  }

  /**
   * Get review questions from a specific subject
   */
  async getReviewQuestionsFromSubject(subjectName, userProgress) {
    try {
      console.log(`   - Getting review questions from ${subjectName}`);
      
      const completedNodes = userProgress?.currentPath?.completedNodes || [];
      const srsData = userProgress?.srsData || {};
      const questionPerformance = userProgress?.questionHistory?.questionPerformance || {};
      
      if (completedNodes.length === 0) {
        return [];
      }
      
      // Get all questions for completed nodes in this subject
      const subjectQuestions = await this.getQuestionsForSubject(subjectName, completedNodes);
      
             // Filter for due reviews in this subject
       const dueReviews = [];
       for (const question of subjectQuestions) {
         const questionSrsData = srsData[question.questionId] || {};
         
         // Convert Firestore Timestamp to Date for comparison
         const nextReview = questionSrsData.nextReview ? 
           (questionSrsData.nextReview.toDate ? questionSrsData.nextReview.toDate() : new Date(questionSrsData.nextReview)) : null;
         
         // Check if review is due
         if (!nextReview || new Date() >= nextReview) {
          dueReviews.push({
            ...question,
            priority: this.calculateReviewPriority(question, questionPerformance, questionSrsData)
          });
        }
      }
      
      // Sort by priority and return top 2
      dueReviews.sort((a, b) => b.priority - a.priority);
      return dueReviews.slice(0, 2);
      
    } catch (error) {
      console.error(`❌ Error getting review questions from ${subjectName}:`, error);
      return [];
    }
  }

  /**
   * Get questions for a specific subject from completed nodes
   */
  async getQuestionsForSubject(subjectName, completedNodes) {
    try {
      // Get learning nodes for this subject
      const learningNodesRef = collection(db, 'learningNodes');
      const q = query(
        learningNodesRef,
        where('nodeType', '==', 'lesson'),
        where('subjectName', '==', subjectName)
      );
      
      const snapshot = await getDocs(q);
      const subjectNodeIds = snapshot.docs.map(doc => doc.id);
      
      // Filter completed nodes to only include this subject
      const subjectCompletedNodes = completedNodes.filter(nodeId => 
        subjectNodeIds.includes(nodeId)
      );
      
      if (subjectCompletedNodes.length === 0) {
        return [];
      }
      
      // Get questions for these nodes
      return await this.getQuestionsForMultipleNodes(subjectCompletedNodes);
      
    } catch (error) {
      console.error(`❌ Error getting questions for subject ${subjectName}:`, error);
      return [];
    }
  }

  /**
   * Adjust session length to match user preference
   */
  adjustSessionLength(nodes, preferredLength) {
    let currentDuration = this.calculateSessionDuration({ nodes });
    
    if (currentDuration <= preferredLength) {
      return nodes; // Session fits within preference
    }
    
    // Trim session to fit preference
    const trimmedNodes = [];
    let accumulatedDuration = 0;
    
    for (const node of nodes) {
      const nodeDuration = this.getNodeDuration(node);
      
      if (accumulatedDuration + nodeDuration <= preferredLength) {
        trimmedNodes.push(node);
        accumulatedDuration += nodeDuration;
      } else {
        // Try to add partial content
        if (node.nodeType === 'micro-practice' || node.nodeType === 'review') {
          const remainingTime = preferredLength - accumulatedDuration;
          const maxQuestions = Math.floor(remainingTime / 2); // 2 minutes per question
          
          if (maxQuestions > 0) {
            trimmedNodes.push({
              ...node,
              content: {
                ...node.content,
                questions: node.content.questions.slice(0, maxQuestions)
              },
              metadata: {
                ...node.metadata,
                questionCount: maxQuestions
              }
            });
          }
        }
        break;
      }
    }
    
    console.log(`⏱️ Adjusted session from ${currentDuration} to ${this.calculateSessionDuration({ nodes: trimmedNodes })} minutes`);
    return trimmedNodes;
  }

  /**
   * Calculate total questions in session
   */
  calculateTotalQuestions(nodes) {
    return nodes.reduce((total, node) => {
      if (node.nodeType === 'micro-practice' || node.nodeType === 'review') {
        return total + (node.metadata?.questionCount || node.content?.questions?.length || 0);
      }
      return total;
    }, 0);
  }

  /**
   * Get node duration
   */
  getNodeDuration(node) {
    if (node.nodeType === 'lesson') {
      return node.metadata?.estimatedTime || 10;
    } else if (node.nodeType === 'micro-practice' || node.nodeType === 'review') {
      const questionCount = node.metadata?.questionCount || node.content?.questions?.length || 0;
      return questionCount * 2; // 2 minutes per question
    }
    return 5; // Default
  }

  /**
   * Calculate session duration
   */
  calculateSessionDuration(session) {
    return session.nodes.reduce((total, node) => total + this.getNodeDuration(node), 0);
  }

  /**
   * Get next subject in rotation (respecting user preferences)
   */
  getNextSubject(currentSubject, userSubjects) {
    const subjectIndex = userSubjects.indexOf(currentSubject);
    if (subjectIndex === -1) return userSubjects[0];
    return userSubjects[(subjectIndex + 1) % userSubjects.length];
  }

  /**
   * Update user progress after session completion with true SM-2 algorithm and transaction safety
   */
  async updateUserProgressAfterSession(userId, session, sessionResults) {
    try {
      console.log('📊 Updating user progress after session');
      
      const userProgressRef = doc(db, 'userProgress', userId);
      
      // Use batch write for transaction safety
      const batch = writeBatch(db);
      
      // Get current progress
      const userProgressDoc = await getDoc(userProgressRef);
      if (!userProgressDoc.exists()) {
        console.log('❌ User progress not found for update');
        return;
      }
      
      const currentProgress = userProgressDoc.data();
      
      // Collect all asked questions for single arrayUnion operation
      const newAskedQuestions = sessionResults.answers.map(answer => answer.questionId);
      
      // Update question history with atomic operations
      batch.update(userProgressRef, {
        [`questionHistory.askedQuestions`]: arrayUnion(...newAskedQuestions)
      });
      
      // Update question performance for each answer
      sessionResults.answers.forEach(answer => {
        batch.update(userProgressRef, {
          [`questionHistory.questionPerformance.${answer.questionId}`]: {
            correct: answer.isCorrect,
            responseTime: answer.responseTime,
            date: serverTimestamp()
          }
        });
      });
      
      // Calculate session statistics
      const totalQuestionsAnswered = (currentProgress.questionHistory?.totalQuestionsAnswered || 0) + sessionResults.answers.length;
      const correctAnswers = (currentProgress.questionHistory?.correctAnswers || 0) + sessionResults.answers.filter(a => a.isCorrect).length;
      const totalResponseTime = sessionResults.answers.reduce((sum, a) => sum + (a.responseTime || 0), 0);
      const averageResponseTime = totalQuestionsAnswered > 0 ? 
        ((currentProgress.questionHistory?.averageResponseTime || 0) * (totalQuestionsAnswered - sessionResults.answers.length) + totalResponseTime) / totalQuestionsAnswered : 0;
      
      // Update question history statistics
      batch.update(userProgressRef, {
        'questionHistory.totalQuestionsAnswered': totalQuestionsAnswered,
        'questionHistory.correctAnswers': correctAnswers,
        'questionHistory.averageResponseTime': averageResponseTime,
        'questionHistory.lastAskedDate': serverTimestamp()
      });
      
      // Update session stats with atomic increments and rolling average (minutes)
      const previousSessionCount = currentProgress.sessionStats?.totalSessions || 0;
      const previousAverageLength = currentProgress.sessionStats?.averageSessionLength || 0;
      const newSessionCount = previousSessionCount + 1;
      const thisSessionMinutes = (sessionResults.duration || 0) / 60;
      const newAverageLength = newSessionCount > 0
        ? ((previousAverageLength * previousSessionCount) + thisSessionMinutes) / newSessionCount
        : thisSessionMinutes;

      batch.update(userProgressRef, {
        'sessionStats.totalSessions': increment(1),
        'sessionStats.totalTime': increment(sessionResults.duration),
        'sessionStats.averageSessionLength': Number(newAverageLength.toFixed(1)),
        'sessionStats.lastSessionDate': serverTimestamp()
      });
      
      // Update subject accuracy - aggregate by subject first
      const subjectAccuracyUpdates = {};
      sessionResults.answers.forEach(answer => {
        const subject = answer.subjectName;
        if (subject) {
          if (!subjectAccuracyUpdates[subject]) {
            const currentAccuracy = currentProgress.performance?.subjectAccuracy?.[subject] || { correct: 0, total: 0, accuracy: 0 };
            subjectAccuracyUpdates[subject] = {
              correct: currentAccuracy.correct,
              total: currentAccuracy.total,
              accuracy: currentAccuracy.accuracy
            };
          }
          
          subjectAccuracyUpdates[subject].total += 1;
          if (answer.isCorrect) {
            subjectAccuracyUpdates[subject].correct += 1;
          }
          subjectAccuracyUpdates[subject].accuracy = subjectAccuracyUpdates[subject].correct / subjectAccuracyUpdates[subject].total;
        }
      });
      
      // Write aggregated accuracy updates
      Object.entries(subjectAccuracyUpdates).forEach(([subject, accuracy]) => {
        batch.update(userProgressRef, {
          [`performance.subjectAccuracy.${subject}`]: accuracy
        });
      });
      
      // Update current path
      const completedNodes = [...(currentProgress.currentPath?.completedNodes || [])];
      const lessonNodes = session.nodes.filter(n => n.nodeType === 'lesson');
      lessonNodes.forEach(node => {
        if (!completedNodes.includes(node.nodeId)) {
          completedNodes.push(node.nodeId);
        }
      });
      
      // Compute and update path progress
      const preferredSubjects = currentProgress?.preferences?.subjects || this.subjects;
      const totalLessons = await this.countTotalLessons(preferredSubjects);
      const pathProgress = totalLessons > 0 ? Math.round((completedNodes.length / totalLessons) * 100) : 0;

      batch.update(userProgressRef, {
        'currentPath.completedNodes': completedNodes,
        'currentPath.currentNodeId': sessionResults.nextNodeId,
        'currentPath.currentSubject': sessionResults.nextSubject,
        'currentPath.pathProgress': pathProgress
      });
      
      // Update SRS data with true SM-2 algorithm
      sessionResults.answers.forEach(answer => {
        const currentSrsData = currentProgress.srsData?.[answer.questionId] || { 
          level: 1, 
          eFactor: 2.5, 
          intervalDays: 1, 
          repetitionCount: 0 
        };
        
        // Calculate quality score (0-5) based on performance
        const quality = this.calculateQualityScore(answer);
        
        // Update SRS data using true SM-2 algorithm
        const newSrsData = this.updateSRSDataSM2(currentSrsData, quality);
        batch.update(userProgressRef, {
          [`srsData.${answer.questionId}`]: newSrsData
        });
      });
      
             // Update streak and XP with server-time consistency
       const now = new Date();
       const lastStudyDate = currentProgress.lastStudyDate;
       
       let newStreak = currentProgress.streak || 0;
       if (lastStudyDate) {
         // Convert Firestore Timestamp to Date for comparison
         const lastStudyDateObj = lastStudyDate.toDate ? lastStudyDate.toDate() : new Date(lastStudyDate);
         const lastStudyDay = lastStudyDateObj.toDateString();
         const today = now.toDateString();
         const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString();
         
         if (lastStudyDay !== today) {
           if (lastStudyDay === yesterday) {
             // Consecutive day
             newStreak += 1;
           } else {
             // Break in streak
             newStreak = 1;
           }
         }
       } else {
         // First study session
         newStreak = 1;
       }
       
       // Calculate XP using the new streak value (not the old one from sessionResults)
       const sessionXP = this.calculateSessionXP(sessionResults, newStreak);
      
      batch.update(userProgressRef, {
        'lastStudyDate': serverTimestamp(),
        'totalXP': (currentProgress.totalXP || 0) + sessionXP,
        'streak': newStreak,
        'updatedAt': serverTimestamp()
      });
      
      // Commit all updates atomically
      await batch.commit();
      console.log('✅ User progress updated successfully with transaction safety');
      
    } catch (error) {
      console.error('❌ Error updating user progress:', error);
      throw error;
    }
  }

  /**
   * Calculate quality score (0-5) for SM-2 algorithm
   */
  calculateQualityScore(answer) {
    // Base score on correctness and response time
    let score = answer.isCorrect ? 3 : 0; // Base score
    
    if (answer.isCorrect) {
      // Bonus for fast responses
      if (answer.responseTime < 30) score += 1; // Very fast
      else if (answer.responseTime < 60) score += 0.5; // Fast
      
      // Bonus for consistent performance
      // This would be enhanced with historical data
    } else {
      // Penalty for very slow wrong answers
      if (answer.responseTime > 120) score -= 1;
    }
    
    // Snap to integer for classic SM-2 consistency
    return Math.max(0, Math.min(5, Math.round(score))); // Clamp between 0 and 5, rounded to integer
  }

  /**
   * Update SRS data using true SM-2 algorithm with interval tracking
   */
  updateSRSDataSM2(currentData, quality) {
    let { level, eFactor, intervalDays, repetitionCount } = currentData;
    
    // SM-2 algorithm with proper interval tracking
    if (quality >= 3) {
      // Correct answer
      repetitionCount += 1;
      
      if (repetitionCount === 1) {
        intervalDays = 1;
        level = 2;
      } else if (repetitionCount === 2) {
        intervalDays = 6;
        level = 3;
      } else {
        // Use previous interval and e-factor
        intervalDays = Math.round(intervalDays * eFactor);
        level += 1;
      }
      
      eFactor = Math.max(1.3, eFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02)));
    } else {
      // Wrong answer
      repetitionCount = 0;
      intervalDays = 1;
      level = 1;
      eFactor = Math.max(1.3, eFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02)));
    }
    
         // Use server timestamp for consistency
     const now = new Date();
     const nextReview = new Date(now.getTime() + intervalDays * 24 * 60 * 60 * 1000);
     
     return {
       level,
       eFactor,
       intervalDays,
       repetitionCount,
       lastReviewed: serverTimestamp(),
       nextReview: Timestamp.fromDate(nextReview) // Explicitly convert to Firestore Timestamp
     };
  }

     /**
    * Calculate XP for session
    */
   calculateSessionXP(sessionResults, currentStreak = 0) {
     let xp = 0;
     
     // Base XP for completing session
     xp += 10;
     
     // XP for correct answers
     const correctAnswers = sessionResults.answers.filter(a => a.isCorrect).length;
     xp += correctAnswers * 5;
     
     // Bonus XP for streak (use the current streak, not the old one from sessionResults)
     if (currentStreak >= 7) xp += 20;
     else if (currentStreak >= 3) xp += 10;
     
     return xp;
   }

  /**
   * Get questions for a specific node (helper method)
   */
  async getQuestionsForNode(nodeId, userId, userProgress) {
    try {
      const questionsRef = collection(db, 'questions');
      const q = query(
        questionsRef,
        where('learningNodeId', '==', nodeId)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        questionId: doc.id,
        ...doc.data()
      }));
      
    } catch (error) {
      console.error('❌ Error getting questions for node:', error);
      return [];
    }
  }
}

export default new SessionBuilderService();

